import { Button, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import SignaturePad from "signature_pad";

const SignaturePadComponent = forwardRef((props, ref) => {
  const { palette } = useTheme();
  const mode = palette?.mode;

  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    signaturePadRef.current = new SignaturePad(canvas);

    if (props.penColor) {
      signaturePadRef.current.penColor = props.penColor;
    }

    return () => {
      signaturePadRef.current.off();
    };
  }, [props.penColor, mode]);

  // Keep the canvas' internal (bitmap) resolution synced with its rendered
  // CSS size × devicePixelRatio. signature_pad records pointer events in
  // raw CSS pixels relative to the canvas' bounding rect — if the bitmap
  // size doesn't match the rendered size (e.g. the canvas is squeezed by a
  // narrow container on mobile), strokes land in the wrong place. Resizing
  // a canvas wipes its bitmap, so any existing stroke data is saved via
  // toData() and restored via fromData() afterwards.
  useEffect(() => {
    const canvas = canvasRef.current;

    const resizeCanvas = () => {
      const pad = signaturePadRef.current;
      const data = pad ? pad.toData() : [];
      const dpr = Math.max(window.devicePixelRatio || 1, 1);
      const rect = canvas.getBoundingClientRect();
      const width = Math.round(rect.width * dpr);
      const height = Math.round(rect.height * dpr);
      if (!width || !height) return;
      if (canvas.width === width && canvas.height === height) return;

      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").scale(dpr, dpr);

      if (pad) {
        pad.clear();
        if (data.length) pad.fromData(data);
      }
    };

    resizeCanvas();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(resizeCanvas);
      resizeObserver.observe(canvas);
    }
    window.addEventListener("resize", resizeCanvas);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", resizeCanvas);
    };
  }, [mode]);

  useImperativeHandle(ref, () => ({
    clear: () => {
      signaturePadRef.current.clear();
    },
    getSignature: () => {
      if (signaturePadRef.current.isEmpty()) {
        return null;
      }
      return signaturePadRef.current.toDataURL();
    },
  }));

  return (
    <div>
      {props.label && (
        <Typography variant="body" gutterBottom color="#666666" margin={"10px"}>
          {props.label}
        </Typography>
      )}
      {/* Border/padding live on the wrapper so the canvas' bounding rect
          matches its bitmap exactly — padding on the canvas itself would
          offset stroke coordinates. */}
      <div
        style={{
          border: "1px solid #ccc",
          borderRadius: "5px",
          padding: "10px",
          background: mode === "dark" ? "#222f3e" : "white",
          display: "inline-block",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            display: "block",
            width: props.width ? props.width : "100%",
            maxWidth: "100%",
            height: props.height || 200,
            touchAction: "none",
            ...props.style,
          }}
        />
      </div>
      <Button onClick={() => signaturePadRef.current.clear()}>Clear</Button>
    </div>
  );
});

export default SignaturePadComponent;
